import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, ThumbsUp, ThumbsDown, RotateCcw, Sparkles, ShieldAlert, ExternalLink, Loader2, ArrowLeft, X, Zap, Heart } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import KaiUpgradeModal from "@/components/KaiUpgradeModal";
import { kaiVerifySkool, kaiRequestCode, kaiVerifyCode, kaiCheckSession, kaiChat, kaiFeedback, kaiReset, kaiMembership, kaiUpgradeStatus, kaiBillingPortal, kaiProactiveList, kaiProactiveDismiss, kaiRecoveryState, kaiRecoveryResolve, adoptToolResult, askKaiAboutResult, askKaiAboutArticle, trackEvent } from "@/lib/api";

const STORAGE_KEY = "kai_session_v2";

const WELCOME_MESSAGE = `Hi, I'm Kai — your GLP-1 companion from MetabolicLabOS.

I'm here for the everyday stuff: side effects, food, muscle, plateaus, motivation, and the parts of this medication that nobody warns you about. Educational only — I won't diagnose you or change your dose.

How is your GLP-1 journey going today?`;

// 7 emotionally-tagged quick-start chips per product spec
const SUGGESTED_PROMPTS = [
  "I hit a plateau",
  "I feel nauseous",
  "I'm losing motivation",
  "I'm scared of regain",
  "I'm proud of myself",
  "I feel overwhelmed",
  "Help me restart",
];

// --- Lightweight markdown renderer: bold + links only ---
const renderInline = (text) => {
  const out = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    if (match[1]) {
      out.push(<strong key={`b${key++}`} className="font-semibold text-[#1A1A1A]">{match[1]}</strong>);
    } else if (match[2] && match[3]) {
      out.push(
        <a
          key={`l${key++}`}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#C95C3E] underline underline-offset-2 hover:text-[#A04A33]"
          data-testid="kai-inline-link"
        >
          {match[2]}
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const MessageBubble = ({ msg, onFeedback }) => {
  const isUser = msg.role === "user";
  const [rated, setRated] = useState(null);

  const handleRate = async (rating) => {
    if (rated) return;
    setRated(rating);
    try {
      await onFeedback(msg.id, rating);
      toast.success(rating === "up" ? "Thanks for the feedback!" : "Got it — I'll try to do better.");
    } catch {
      setRated(null);
      toast.error("Couldn't save feedback");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}
      data-testid={`kai-message-${msg.role}`}
    >
      <div className={`flex gap-3 max-w-[85%] ${isUser ? "flex-row-reverse" : "flex-row"}`}>
        {!isUser && (
          <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "#2D6A4F" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex flex-col">
          <div
            className={`rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
              isUser ? "text-white" : "text-[#1A1A1A] bg-white border border-[#E8E4DC]"
            } ${msg.red_flag && !isUser ? "border-red-300 bg-red-50" : ""}`}
            style={isUser ? { backgroundColor: "#2D6A4F" } : {}}
          >
            {msg.red_flag && !isUser && (
              <div className="flex items-center gap-2 mb-2 text-red-700 font-semibold text-sm" data-testid="kai-redflag-banner">
                <ShieldAlert className="w-4 h-4" />
                Urgent: seek medical help
              </div>
            )}
            <div>{renderInline(msg.content)}</div>
          </div>

          {!isUser && !msg.pending && (
            <div className="flex items-center gap-1 mt-1.5 px-1">
              <button
                onClick={() => handleRate("up")}
                disabled={!!rated}
                data-testid="kai-thumbs-up"
                aria-label="Helpful"
                className={`p-1.5 rounded-lg transition-colors ${
                  rated === "up" ? "bg-[#2D6A4F] text-white" : "text-[#8A7D55] hover:bg-[#E8E4DC] disabled:opacity-40"
                }`}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleRate("down")}
                disabled={!!rated}
                data-testid="kai-thumbs-down"
                aria-label="Not helpful"
                className={`p-1.5 rounded-lg transition-colors ${
                  rated === "down" ? "bg-[#C95C3E] text-white" : "text-[#8A7D55] hover:bg-[#E8E4DC] disabled:opacity-40"
                }`}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const AccessGate = ({ isSkool, onSuccess, variant = "page", onClose }) => {
  const [step, setStep] = useState(1); // 1=email (or email+code for skool), 2=enter code (email flow only)
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [skoolCode, setSkoolCode] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submitSkool = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !skoolCode) { setError("Email and access code are required."); return; }
    setLoading(true);
    try {
      const res = await kaiVerifySkool({ email: email.trim(), name: name.trim() || null, access_code: skoolCode.trim() });
      onSuccess({
        session_id: res.data.session_id,
        email: res.data.email,
        expires_at: res.data.expires_at,
        source_of_user: res.data.source_of_user,
      });
    } catch (err) {
      // Skool-code and verify-code lockouts are 15 min after 5 failed attempts.
      if (err?.response?.status === 429) setError("Too many attempts. Please wait 15 minutes.");
      else if (err?.response?.status === 401) setError("Invalid access code. Check your Skool community link.");
      else setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const submitRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) { setError("Email is required."); return; }
    setLoading(true);
    try {
      await kaiRequestCode({ email: email.trim(), name: name.trim() || null });
      setStep(2);
      setResendIn(30);
      toast.success("Code sent! Check your email.");
    } catch (err) {
      // /request-code has TWO rate limits: 30s min between requests, and a 15-min
      // lockout after 5 failed attempts. Since the API returns 429 for both,
      // surface the shorter wait — resending in 30s is the common case.
      if (err?.response?.status === 429) setError("Please wait a moment before requesting a new code.");
      else setError("Couldn't send the code. Please try again.");
    } finally { setLoading(false); }
  };

  const submitVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(otp)) { setError("Enter the 6-digit code from your email."); return; }
    setLoading(true);
    try {
      const res = await kaiVerifyCode({ email: email.trim(), code: otp.trim(), name: name.trim() || null });
      onSuccess({
        session_id: res.data.session_id,
        email: res.data.email,
        expires_at: res.data.expires_at,
        source_of_user: res.data.source_of_user,
      });
    } catch (err) {
      if (err?.response?.status === 429) setError("Too many attempts. Please wait 15 minutes.");
      else if (err?.response?.status === 401) setError("Invalid or expired code. Request a new one.");
      else setError("Couldn't verify code. Please try again.");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    setLoading(true);
    try {
      await kaiRequestCode({ email: email.trim(), name: name.trim() || null });
      setResendIn(30);
      toast.success("New code sent.");
    } catch {
      toast.error("Couldn't resend. Try again.");
    } finally { setLoading(false); }
  };

  const containerStyle = { backgroundColor: "#F0EDE6" };
  const inputStyle = { borderColor: "#E8E4DC", backgroundColor: "#FAFAF8", color: "#3D3D3D" };

  const wrapperClass = variant === "popup"
    ? "h-full w-full overflow-y-auto flex items-center justify-center px-4 py-6"
    : "min-h-screen flex items-center justify-center px-4 py-10";

  return (
    <div className={wrapperClass} style={containerStyle} data-testid="kai-email-gate">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#E8E4DC] relative">
          {variant === "popup" && onClose && (
            <button onClick={onClose} aria-label="Close" data-testid="kai-popup-gate-close"
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#F0EDE6] transition-colors" style={{ color: "#8A7D55" }}>
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#2D6A4F" }}>
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: "#1A1A1A", fontFamily: "Fraunces, serif" }}>Meet Kai</h1>
              <p className="text-xs" style={{ color: "#8A7D55" }} data-testid="kai-gate-subtitle">
                {isSkool ? "Your GLP-1 coach · Skool community" : "Your GLP-1 coach"}
              </p>
            </div>
          </div>

          <p className="text-sm mb-6 leading-relaxed" style={{ color: "#4B5548" }} data-testid="kai-gate-intro">
            {isSkool
              ? "Enter your email and Skool access code to use Kai."
              : step === 1
                ? "Enter your email to receive your access code."
                : `We sent a 6-digit code to ${email}. Enter it below.`}
          </p>

          {isSkool && (
            <form onSubmit={submitSkool} className="space-y-3" data-testid="kai-gate-form-skool">
              <div>
                <label htmlFor="kai-name" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>First name (optional)</label>
                <input id="kai-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jamie" data-testid="kai-gate-name-input"
                  className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none" style={inputStyle} />
              </div>
              <div>
                <label htmlFor="kai-email" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>Email</label>
                <input id="kai-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="kai-gate-email-input"
                  className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none" style={inputStyle} />
              </div>
              <div>
                <label htmlFor="kai-pw" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>Skool access code</label>
                <input id="kai-pw" type="password" value={skoolCode} onChange={(e) => setSkoolCode(e.target.value)} placeholder="Enter community access code" required data-testid="kai-gate-password-input"
                  className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none" style={inputStyle} />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="kai-gate-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="kai-gate-submit-button"
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50 mt-2"
                style={{ backgroundColor: "#2D6A4F" }}>
                {loading ? "Verifying..." : "Enter Kai"}
              </button>
            </form>
          )}

          {!isSkool && step === 1 && (
            <form onSubmit={submitRequestCode} className="space-y-3" data-testid="kai-gate-form-email-step1">
              <div>
                <label htmlFor="kai-name" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>First name (optional)</label>
                <input id="kai-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jamie" data-testid="kai-gate-name-input"
                  className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none" style={inputStyle} />
              </div>
              <div>
                <label htmlFor="kai-email" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>Email</label>
                <input id="kai-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="kai-gate-email-input"
                  className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none" style={inputStyle} />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="kai-gate-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="kai-gate-submit-button"
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50 mt-2"
                style={{ backgroundColor: "#2D6A4F" }}>
                {loading ? "Sending code..." : "Send me a code"}
              </button>
            </form>
          )}

          {!isSkool && step === 2 && (
            <form onSubmit={submitVerifyCode} className="space-y-3" data-testid="kai-gate-form-email-step2">
              <button type="button" onClick={() => { setStep(1); setOtp(""); setError(""); }} data-testid="kai-gate-back"
                className="flex items-center gap-1 text-xs mb-1" style={{ color: "#8A7D55" }}>
                <ArrowLeft className="w-3 h-3" /> Use a different email
              </button>
              <div>
                <label htmlFor="kai-otp" className="block text-xs font-medium mb-1" style={{ color: "#3D3D3D" }}>6-digit code</label>
                <input id="kai-otp" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456" required data-testid="kai-gate-otp-input" autoComplete="one-time-code"
                  className="w-full px-4 py-3 rounded-xl text-lg tracking-widest text-center border focus:outline-none font-mono" style={inputStyle} />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="kai-gate-error">{error}</p>}
              <button type="submit" disabled={loading || otp.length !== 6} data-testid="kai-gate-verify-button"
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50 mt-2"
                style={{ backgroundColor: "#2D6A4F" }}>
                {loading ? "Verifying..." : "Verify & enter"}
              </button>
              <button type="button" onClick={resend} disabled={resendIn > 0 || loading} data-testid="kai-gate-resend"
                className="w-full py-2 text-xs font-medium transition-opacity disabled:opacity-40"
                style={{ color: "#8A7D55" }}>
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            </form>
          )}

          <p className="text-[11px] mt-5 leading-relaxed text-center" style={{ color: "#8A7D55" }}>
            Educational only. Not medical advice. By entering you agree to our{" "}
            <a href="/terms" className="underline">Terms</a> and{" "}
            <a href="/privacy" className="underline">Privacy Policy</a>.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const KaiChat = ({ variant = "page", onClose }) => {
  const [searchParams] = useSearchParams();
  // In popup mode we always use the non-Skool (email+OTP) flow unless user
  // navigates to /kai?src=skool explicitly
  const isSkool = variant === "page" && searchParams.get("src") === "skool";
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s?.expires_at || new Date(s.expires_at) <= new Date()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return s;
    } catch { return null; }
  });
  const [sessionChecked, setSessionChecked] = useState(!session);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [membership, setMembership] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("limit"); // "limit" | "soft"
  const [proactive, setProactive] = useState([]);
  const [recovery, setRecovery] = useState(null); // {active, trigger, opener, started_at}
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Load membership when session becomes active + handle ?upgrade=success
  useEffect(() => {
    if (!session?.email) return;
    kaiMembership({ email: session.email }).then(r => setMembership(r.data)).catch(() => {});
  }, [session?.email]);

  // Fetch proactive companion messages on-demand when session opens
  useEffect(() => {
    if (!session?.session_id || !session?.email) return;
    kaiProactiveList(session.session_id, session.email)
      .then(r => setProactive(r.data?.messages || []))
      .catch(() => {});
    kaiRecoveryState(session.session_id, session.email)
      .then(r => setRecovery(r.data))
      .catch(() => {});
  }, [session?.session_id, session?.email]);

  const resolveRecovery = async () => {
    if (!session) return;
    try {
      await kaiRecoveryResolve({ session_id: session.session_id, email: session.email });
      setRecovery((prev) => ({ ...(prev || {}), active: false }));
      toast.success("Back on rhythm. Kai's regular tone is on.");
    } catch {
      toast.error("Couldn't update recovery state.");
    }
  };

  const dismissProactive = async (messageId) => {
    setProactive((prev) => prev.filter((m) => m.id !== messageId));
    try {
      await kaiProactiveDismiss({
        session_id: session.session_id,
        email: session.email,
        message_id: messageId,
      });
    } catch { /* silent — UI already updated */ }
  };

  // Post-checkout return — resolve upgrade
  useEffect(() => {
    if (variant !== "page") return;
    const upgradeFlag = searchParams.get("upgrade");
    const passesFlag = searchParams.get("passes");
    const sid = searchParams.get("session_id");
    if ((upgradeFlag === "success" || passesFlag === "success") && sid) {
      (async () => {
        try {
          const r = await kaiUpgradeStatus(sid);
          if (r.data?.payment_status === "paid") {
            if (r.data.is_kai_upgrade) {
              toast.success("You now have unlimited access to Kai. Ask anything anytime.");
            } else if (r.data.is_kai_passes) {
              toast.success(`${r.data.passes_granted} extra messages added to your account.`);
            }
            if (session?.email) {
              const m = await kaiMembership({ email: session.email });
              setMembership(m.data);
            }
          }
        } catch { /* ignore */ }
        window.history.replaceState({}, "", "/kai");
      })();
    } else if (upgradeFlag === "cancel" || passesFlag === "cancel") {
      toast("Checkout cancelled.");
      window.history.replaceState({}, "", "/kai");
    }
  }, [variant, searchParams, session?.email]);

  // Validate persisted session against backend once on mount
  useEffect(() => {
    if (!session || sessionChecked) return;
    (async () => {
      try {
        const res = await kaiCheckSession({ session_id: session.session_id, email: session.email });
        if (!res.data?.valid) {
          localStorage.removeItem(STORAGE_KEY);
          setSession(null);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setSession(null);
      } finally {
        setSessionChecked(true);
      }
    })();
  }, [session, sessionChecked]);

  useEffect(() => {
    if (session) {
      setMessages([{ id: "welcome", role: "assistant", content: WELCOME_MESSAGE }]);
    }
  }, [session?.session_id]);

  // useRef guard for StrictMode + localStorage set for cross-reload persistence.
  // Ref handles the double-invoke within the same mount; localStorage stops a
  // user from reloading /kai?tool_result=<id> to burn additional free messages.
  const toolResultProcessedRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const handleGateSuccess = (s) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSession(s);
    setSessionChecked(true);
  };

  const sendMessage = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending || !session) return;
    const userId = `u-${Date.now()}`;
    setMessages((m) => [...m, { id: userId, role: "user", content: msg }]);
    setInput("");
    setSending(true);

    // Placeholder pending bubble
    const pendingId = `p-${Date.now()}`;
    setMessages((m) => [...m, { id: pendingId, role: "assistant", content: "Thinking…", pending: true }]);

    try {
      const res = await kaiChat({
        session_id: session.session_id,
        email: session.email,
        message: msg,
      });
      setMessages((m) => m.filter((x) => x.id !== pendingId).concat({
        id: res.data.message_id,
        role: "assistant",
        content: res.data.reply,
        red_flag: res.data.red_flag,
      }));
      if (res.data.membership) setMembership((prev) => ({ ...(prev || {}), ...res.data.membership }));
      // Phase 2: reflect recovery state coming back from chat
      if (res.data.recovery) {
        setRecovery((prev) => ({ ...(prev || {}), ...res.data.recovery }));
        if (res.data.recovery.just_entered) {
          toast("Kai shifted to a softer tone for you.", { description: "No streak pressure. One tiny thing today is enough." });
        }
        if (res.data.recovery.just_resolved) {
          toast.success("Back on rhythm. Kai's regular tone is on.");
        }
      }
      // Phase 2: surface a freshly-earned companion moment as a positive toast
      if (Array.isArray(res.data.new_moments) && res.data.new_moments.length > 0) {
        const m = res.data.new_moments[0];
        toast(m.copy, { description: "A companion moment from Kai." });
      }
    } catch (err) {
      // Handle 402 (limit reached) specially — show hard upgrade modal, don't treat as error
      if (err?.response?.status === 402) {
        setMessages((m) => m.filter((x) => x.id !== pendingId));
        const detail = err.response.data?.detail;
        if (detail?.pricing) setMembership((prev) => ({ ...(prev || {}), pricing: detail.pricing, can_send: false }));
        setUpgradeReason("limit");
        setUpgradeOpen(true);
      } else {
        setMessages((m) => m.filter((x) => x.id !== pendingId).concat({
          id: `err-${Date.now()}`,
          role: "assistant",
          content: "Sorry — I had trouble answering that. Please try again in a moment.\n\nEducational only. Not medical advice.",
        }));
        if (err?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setSession(null);
          toast.error("Session expired. Please sign in again.");
        }
      }
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }, [sending, session]);

  // Tool-result handoff: consume ?tool_result=<id>. Once authenticated we adopt
  // (attach anonymous result to member), then fetch server-side prefill and
  // send it as the user's first turn so Kai responds in context. NEVER put
  // health text in the URL — the id is opaque and the report is fetched
  // server-side via ask-kai endpoint after auth.
  useEffect(() => {
    if (variant !== "page") return;
    if (!session?.session_id || !session?.email) {
      // Anonymous with tool_result param: persist so we can pick it back up
      // after the user completes the OTP flow — result is not lost.
      const trId = searchParams.get("tool_result");
      if (trId) {
        try {
          sessionStorage.setItem("mlos.pending_tool_result", JSON.stringify({
            result_id: trId, at: Date.now(),
          }));
        } catch { /* private mode */ }
      }
      return;
    }
    let trId = searchParams.get("tool_result");
    if (!trId) {
      try {
        const stashed = JSON.parse(sessionStorage.getItem("mlos.pending_tool_result") || "null");
        if (stashed?.result_id) trId = stashed.result_id;
      } catch { /* ignore */ }
    }
    if (!trId) return;
    // Guard against StrictMode double-invocation and per-result_id replays.
    if (toolResultProcessedRef.current === trId) return;
    // Persistent guard: if the user has already asked Kai about this exact
    // result_id in a prior session, do not re-send the prefill. Clean the URL
    // so it doesn't look "stuck".
    try {
      const done = JSON.parse(localStorage.getItem("mlos.tool_results_asked") || "[]");
      if (Array.isArray(done) && done.includes(trId)) {
        toolResultProcessedRef.current = trId;
        try { sessionStorage.removeItem("mlos.pending_tool_result"); } catch { /* ignore */ }
        window.history.replaceState({}, "", "/kai");
        return;
      }
    } catch { /* corrupt storage - fall through, will be overwritten */ }
    toolResultProcessedRef.current = trId;
    (async () => {
      try {
        try {
          await adoptToolResult(trId, { email: session.email, session_id: session.session_id });
        } catch (adoptErr) {
          const status = adoptErr?.response?.status;
          if (status === 403 || status === 404) {
            try { sessionStorage.removeItem("mlos.pending_tool_result"); } catch { /* ignore */ }
            window.history.replaceState({}, "", "/kai");
            return;
          }
        }
        const followup = searchParams.get("followup") === "1";
        const prefillResp = await askKaiAboutResult(trId, {
          email: session.email, session_id: session.session_id, followup,
        });
        const { prefill, tool_id: tId } = prefillResp.data || {};
        if (prefill) {
          // Client analytics — matches server-side event choice (§Item 1).
          trackEvent(
            followup ? "diagnostic_followup_clicked" : "ask_kai_clicked",
            { tool_id: tId, result_id: trId, authenticated: true }
          ).catch(() => {});
          try { sessionStorage.removeItem("mlos.pending_tool_result"); } catch { /* ignore */ }
          // Persist so reloading the same URL doesn't re-send the prefill.
          try {
            const done = JSON.parse(localStorage.getItem("mlos.tool_results_asked") || "[]");
            if (!done.includes(trId)) {
              done.push(trId);
              // Cap the list so it doesn't grow forever.
              const trimmed = done.slice(-50);
              localStorage.setItem("mlos.tool_results_asked", JSON.stringify(trimmed));
            }
          } catch { /* ignore */ }
          window.history.replaceState({}, "", "/kai");
          sendMessage(prefill);
        }
      } catch (e) { /* silent */ }
    })();
  }, [session?.session_id, session?.email, variant, searchParams, sendMessage]);

  // Article → Ask-Kai direct edge. Same pattern as tool_result handoff but
  // uses a public article slug (not a private opaque id). Auth is still
  // required so Kai's daily message budget belongs to a real member.
  const articleAskedRef = useRef(null);
  useEffect(() => {
    if (variant !== "page") return;
    const slug = searchParams.get("from_article");
    if (!slug) return;
    if (!session?.session_id || !session?.email) {
      // Persist so we can pick it back up after auth. Article slug is public
      // so this is safe to place in sessionStorage.
      try {
        sessionStorage.setItem("mlos.pending_article", JSON.stringify({
          article_slug: slug, at: Date.now(),
        }));
      } catch { /* private mode */ }
      return;
    }
    // Prefer freshly-stashed slug if we came out of auth
    let effectiveSlug = slug;
    try {
      const stashed = JSON.parse(sessionStorage.getItem("mlos.pending_article") || "null");
      if (stashed?.article_slug) effectiveSlug = stashed.article_slug;
    } catch { /* ignore */ }
    if (articleAskedRef.current === effectiveSlug) return;
    articleAskedRef.current = effectiveSlug;
    (async () => {
      try {
        const resp = await askKaiAboutArticle({
          email: session.email, session_id: session.session_id, article_slug: effectiveSlug,
        });
        const prefill = resp?.data?.prefill;
        if (prefill) {
          trackEvent("article_to_kai_clicked", {
            article_slug: effectiveSlug, authenticated: true,
          }).catch(() => {});
          try { sessionStorage.removeItem("mlos.pending_article"); } catch { /* ignore */ }
          window.history.replaceState({}, "", "/kai");
          sendMessage(prefill);
        }
      } catch { /* silent — Kai still opens with the normal welcome */ }
    })();
  }, [session?.session_id, session?.email, variant, searchParams, sendMessage]);



  const handleFeedback = async (messageId, rating) => {
    await kaiFeedback({ message_id: messageId, session_id: session.session_id, rating });
  };

  const handleReset = async () => {
    if (!session) return;
    try {
      const res = await kaiReset({ session_id: session.session_id, email: session.email });
      const newSession = {
        session_id: res.data.session_id,
        email: session.email,
        expires_at: res.data.expires_at || session.expires_at,
        source_of_user: session.source_of_user,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
      setSession(newSession);
      setMessages([{ id: "welcome", role: "assistant", content: WELCOME_MESSAGE }]);
      toast.success("Started a fresh chat.");
    } catch {
      toast.error("Couldn't reset. Try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!session) {
    return (
      <>
        {variant === "page" && <SEO title="Kai — Your GLP-1 Coach | MetabolicLabOS" description="Kai is a private AI coach for GLP-1 users. Educational support for side effects, nutrition, and plateaus." noIndex={true} />}
        <AccessGate isSkool={isSkool} onSuccess={handleGateSuccess} variant={variant} onClose={onClose} />
      </>
    );
  }

  // While session is being revalidated, show a subtle loader to avoid flashing the gate
  if (!sessionChecked) {
    return (
      <div className={variant === "popup" ? "h-full flex items-center justify-center" : "min-h-screen flex items-center justify-center"} style={{ backgroundColor: "#F0EDE6" }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} />
      </div>
    );
  }

  return (
    <>
      <SEO title="Kai — Your GLP-1 Coach | MetabolicLabOS" description="Chat privately with Kai, your GLP-1 coach." noIndex={true} />
      <div className="h-screen flex flex-col" style={{ backgroundColor: "#F0EDE6" }} data-testid="kai-chat-page">
        {/* Header */}
        <header className="flex-shrink-0 border-b" style={{ backgroundColor: "#FAFAF8", borderColor: "#E8E4DC" }}>
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#2D6A4F" }}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold leading-tight" style={{ color: "#1A1A1A", fontFamily: "Fraunces, serif" }}>Kai</h1>
                <p className="text-[11px]" style={{ color: "#8A7D55" }}>GLP-1 coach · MetabolicLabOS</p>
              </div>
              {/* Usage pill — free users only */}
              {membership?.kai_plan === "free" && (
                <button
                  onClick={() => { setUpgradeReason("soft"); setUpgradeOpen(true); }}
                  data-testid="kai-usage-pill"
                  className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors hover:bg-[#F0EDE6]"
                  style={{ borderColor: "#E8E4DC", color: membership?.messages_remaining === 0 ? "#C95C3E" : "#4B5548", backgroundColor: "#FAFAF8" }}
                  title="Click to upgrade"
                >
                  {(membership?.message_credits ?? 0) > 0
                    ? <>{membership.message_credits} extra left</>
                    : <>{membership?.messages_today ?? 0}/{membership?.daily_limit ?? 3} today</>}
                </button>
              )}
              {/* Paid badge — clickable → billing portal */}
              {membership?.kai_plan === "paid" && (
                <button
                  onClick={async () => {
                    try {
                      const r = await kaiBillingPortal({ email: session.email });
                      if (r.data?.url) window.location.href = r.data.url;
                    } catch { toast.error("Couldn't open billing portal."); }
                  }}
                  data-testid="kai-manage-sub"
                  className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors hover:bg-[#F0EDE6]"
                  style={{ borderColor: "#2D6A4F", color: "#2D6A4F", backgroundColor: "#FAFAF8" }}
                  title="Manage subscription"
                >
                  Unlimited · Manage
                </button>
              )}
            </div>
            <button
              onClick={handleReset}
              data-testid="kai-reset-button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-[#E8E4DC]"
              style={{ color: "#3D3D3D" }}
              title="Start fresh chat"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        </header>

        {/* Messages */}
        <main ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="kai-messages-scroll">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {/* Phase 2: Recovery-mode banner — softer chat header */}
            {recovery?.active && (
              <div
                className="mb-4 rounded-xl p-3 flex items-start gap-3 border"
                style={{ backgroundColor: "#FAF3E8", borderColor: "#E8D6B6" }}
                data-testid="kai-recovery-mode-banner"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#8A6B3B" }}>
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: "#8A6B3B" }}>Soft mode</p>
                  <p className="text-sm leading-relaxed" style={{ color: "#3D3D3D" }}>
                    {recovery.opener || "Kai is in a gentler tone right now — no streak pressure, no catch-up framing."}
                  </p>
                </div>
                <button
                  onClick={resolveRecovery}
                  data-testid="kai-recovery-resolve"
                  className="flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-md hover:bg-white transition-colors"
                  style={{ color: "#8A6B3B", border: "1px solid #E8D6B6" }}
                >
                  I'm okay
                </button>
              </div>
            )}

            {/* Proactive companion nudges — fetched on session open */}
            {proactive.length > 0 && (
              <div className="mb-4 space-y-2" data-testid="kai-proactive-banners">
                {proactive.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl p-3 flex items-start gap-3 border"
                    style={{ backgroundColor: "#EAF4EE", borderColor: "#B7D8C2" }}
                    data-testid={`kai-proactive-${p.trigger}`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#2D6A4F" }}>
                      <Heart className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: "#2D6A4F" }}>From Kai</p>
                      <p className="text-sm leading-relaxed" style={{ color: "#1A1A1A" }}>{p.copy}</p>
                    </div>
                    <button
                      onClick={() => dismissProactive(p.id)}
                      aria-label="Dismiss"
                      data-testid="kai-proactive-dismiss"
                      className="flex-shrink-0 p-1 rounded-md hover:bg-white transition-colors"
                      style={{ color: "#4B5548" }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} onFeedback={handleFeedback} />
            ))}

            {/* Soft upgrade prompt — shown when free user has used 2 of 3 messages */}
            {membership?.kai_plan === "free" && membership?.soft_prompt && !upgradeOpen && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 mb-4 rounded-xl p-3 flex items-center gap-3 border"
                style={{ backgroundColor: "#FFF4E6", borderColor: "#F3D9B1" }}
                data-testid="kai-soft-upgrade-prompt"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#C95C3E" }}>
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight" style={{ color: "#1A1A1A" }}>
                    1 free message left today
                  </p>
                  <p className="text-xs" style={{ color: "#6B6B6B" }}>
                    If you want unlimited help and clear next steps anytime, you can unlock Kai.
                  </p>
                </div>
                <button
                  onClick={() => { setUpgradeReason("soft"); setUpgradeOpen(true); }}
                  data-testid="kai-soft-upgrade-btn"
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white whitespace-nowrap"
                  style={{ backgroundColor: "#2D6A4F" }}
                >
                  Unlock Kai
                </button>
              </motion.div>
            )}

            {/* Suggested prompts — only when just welcome is shown */}
            {messages.length === 1 && (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-2">
                  <p className="text-xs font-medium mb-2" style={{ color: "#8A7D55" }}>Quick start — pick what matches today:</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_PROMPTS.map((p) => (
                      <button
                        key={p}
                        onClick={() => sendMessage(p)}
                        data-testid="kai-suggested-prompt"
                        className="px-3 py-2 rounded-full text-xs font-medium border transition-colors hover:bg-white"
                        style={{ borderColor: "#E8E4DC", backgroundColor: "#FAFAF8", color: "#3D3D3D" }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </main>

        {/* Sticky input */}
        <footer className="flex-shrink-0 border-t" style={{ backgroundColor: "#FAFAF8", borderColor: "#E8E4DC" }}>
          <div className={`${variant === "popup" ? "px-4" : "max-w-3xl mx-auto px-4"} py-3`}>
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tell Kai how your GLP-1 journey is going today."
                rows={1}
                disabled={sending}
                data-testid="kai-input-textarea"
                className="flex-1 resize-none px-4 py-3 rounded-2xl text-[15px] border focus:outline-none max-h-36"
                style={{ borderColor: "#E8E4DC", backgroundColor: "white", color: "#1A1A1A", minHeight: "48px" }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={sending || !input.trim()}
                data-testid="kai-send-button"
                aria-label="Send"
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40"
                style={{ backgroundColor: "#2D6A4F" }}
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-[10px] text-center mt-2" style={{ color: "#8A7D55" }}>
              Educational only. Not medical advice. Red-flag symptoms → contact your clinician or 911.
            </p>
          </div>
        </footer>
      </div>

      <AnimatePresence>
        {upgradeOpen && (
          <KaiUpgradeModal
            email={session?.email}
            pricing={membership?.pricing}
            passesPrice={membership?.passes_price ?? 2}
            passesCount={membership?.passes_per_purchase ?? 10}
            reason={upgradeReason}
            onClose={() => setUpgradeOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default KaiChat;
